import { Slot, Slottable } from "@radix-ui/react-slot";
import * as React from "react";

import { Spinner } from "@shared/components/Spinner";
import { cn } from "@ui/lib";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";

const buttonVariants = cva(
	"flex items-center justify-center border font-semibold enabled:cursor-pointer transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&>svg]:mr-1.5 [&>svg]:opacity-60",
	{
		variants: {
			variant: {
				primary:
					"border-primary bg-primary text-primary-foreground shadow-[inset_0_0.5px_2px_rgba(255,255,255,0.3)] hover:bg-primary/90",
				error: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
				outline:
					"border-secondary/15 bg-transparent text-secondary hover:bg-secondary/10",
				secondary:
					"border-secondary bg-secondary text-secondary-foreground shadow-[inset_0_0.5px_2px_rgba(255,255,255,0.3)] hover:bg-secondary/90",
				ghost: "border-transparent text-primary hover:bg-primary/10 hover:text-primary",
				link: "border-transparent text-primary underline-offset-4 hover:underline",
			},
			size: {
				md: "h-11 rounded-md px-4 text-sm",
				sm: "h-10 rounded-md px-3 text-xs min-h-[44px]",
				lg: "h-12 rounded-md px-6 text-base",
				icon: "size-11 rounded-md [&>svg]:m-0 [&>svg]:opacity-100 min-w-[44px] min-h-[44px]",
			},
		},
		defaultVariants: {
			variant: "secondary",
			size: "md",
		},
	},
);

export type ButtonProps = {
	asChild?: boolean;
	loading?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
	VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			children,
			variant,
			size,
			asChild = false,
			loading,
			disabled,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(
					buttonVariants({ variant, size, className }),
					// Spinner 常驻后它永远是第一个 svg，所以只在 loading 时盖掉
					// 调用方自己传进来的图标
					loading && "[&>svg+svg]:hidden",
				)}
				ref={ref}
				disabled={disabled || loading}
				{...props}
			>
				{/* Spinner 常驻 DOM，只切 class：按 loading 插拔节点的话，
				    开了浏览器自动翻译的页面（我们没做本地化的语种，用户会被
				    Chrome 提示翻译）会因为 React 持有的文本节点已被翻译器
				    换掉，在 insertBefore 时抛 NotFoundError 崩掉整页 */}
				<Spinner
					className={cn(
						"mr-1.5 size-4 text-inherit",
						!loading && "hidden",
					)}
				/>
				<Slottable>{children}</Slottable>
			</Comp>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
